import React from 'react';

interface JobStatusBadgeProps {
  status: string;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export const JobStatusBadge: React.FC<JobStatusBadgeProps> = ({ 
  status, 
  onClick,
  size = 'md' 
}) => {
  const getStatusColor = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; border: string }> = {
      lead: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' },
      quoted: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
      scheduled: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
      in_progress: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
      completed: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
      cancelled: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
      on_hold: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
    };

    return statusMap[status.toLowerCase()] || statusMap.lead;
  };

  const getStatusLabel = (status: string) => {
    const labelMap: Record<string, string> = {
      lead: 'Lead',
      quoted: 'Quoted',
      scheduled: 'Scheduled',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      on_hold: 'On Hold',
    };

    return labelMap[status.toLowerCase()] || status;
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };

  const colors = getStatusColor(status);
  const label = getStatusLabel(status);

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full border font-medium
        ${colors.bg} ${colors.text} ${colors.border}
        ${sizeClasses[size]}
        ${onClick ? 'cursor-pointer hover:brightness-110 transition-all' : ''}
      `}
      onClick={onClick}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
      {label}
    </span>
  );
};

interface StatusOption {
  value: string;
  label: string;
  description: string;
}

const statusOptions: StatusOption[] = [
  { value: 'lead', label: 'Lead', description: 'Initial contact or inquiry' },
  { value: 'quoted', label: 'Quoted', description: 'Quote sent to client' },
  { value: 'scheduled', label: 'Scheduled', description: 'Work scheduled' },
  { value: 'in_progress', label: 'In Progress', description: 'Work in progress' },
  { value: 'completed', label: 'Completed', description: 'Work completed' },
  { value: 'on_hold', label: 'On Hold', description: 'Temporarily paused' },
  { value: 'cancelled', label: 'Cancelled', description: 'Job cancelled' },
];

interface JobStatusSelectorProps {
  currentStatus: string;
  onStatusChange: (status: string, notes?: string) => void;
  onClose: () => void;
}

export const JobStatusSelector: React.FC<JobStatusSelectorProps> = ({
  currentStatus,
  onStatusChange,
  onClose,
}) => {
  const [selectedStatus, setSelectedStatus] = React.useState(currentStatus);
  const [notes, setNotes] = React.useState('');

  const handleSubmit = () => {
    if (selectedStatus !== currentStatus) {
      onStatusChange(selectedStatus, notes);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a2332] rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Update Job Status</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Current Status */}
          <div className="flex items-center gap-3 p-3 bg-[#101922] rounded-lg border border-gray-700">
            <span className="text-sm text-gray-400">Current Status:</span>
            <JobStatusBadge status={currentStatus} size="sm" />
          </div>

          {/* Status Options */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              New Status
            </label>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <label
                  key={option.value}
                  className={`
                    flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all
                    ${
                      selectedStatus === option.value
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-700 bg-[#101922] hover:border-gray-600'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={selectedStatus === option.value}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{option.label}</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-0.5">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note about this status change..."
              className="w-full px-4 py-2.5 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedStatus === currentStatus}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Update Status
          </button>
        </div>
      </div>
    </div>
  );
};

